import { Badge } from '@/components/shadcn/badge'
import { ContentDataType, FieldDefinition } from '../types-v6'
import { Statement } from './Statement'

export const ItemColumnsLayout = ({
  columnFields,
  content,
  canEditContent,
  canDeleteContent,
}: {
  columnFields: FieldDefinition[]
  content: ContentDataType
  canEditContent: boolean
  canDeleteContent: boolean
}) => {
  // Helper function to render field value based on type and property ID
  const renderFieldValue = (field: FieldDefinition, content: ContentDataType) => {
    const propertyId = field.propertyId
    const value = content[propertyId]

    if (value === undefined || value === null) {
      return <div className="italic text-muted-foreground">No data</div>
    }

    if (typeof value === 'string') {
      return <div className="whitespace-pre-wrap">{value}</div>
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return <div>{String(value)}</div>
    }

    if (Array.isArray(value)) {
      // Handle array of primitive values
      if (value.every((item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')) {
        return (
          <div className="flex flex-wrap gap-2">
            {value.map((item, i) => (
              <Badge key={i} variant="outline">
                {String(item)}
              </Badge>
            ))}
          </div>
        )
      }

      // Handle array of objects (like statements)
      if (field.fieldConfig.editing.type === 'array' && field.fields) {
        const contentFields = field.fields.filter((ff) => ff.fieldConfig.layout.position === 'content')
        const headerFields = field.fields.filter((ff) => ff.fieldConfig.layout.position === 'header')
        return (
          <div className="flex flex-col gap-5">
            {value.map((item, _i) => (
              <Statement
                parentId={content.id}
                key={item.id}
                item={item as ContentDataType}
                headerFields={headerFields}
                contentFields={contentFields}
                canEditContent={canEditContent}
                canDeleteContent={canDeleteContent}
                field={field}
              />
            ))}
          </div>
        )
      }
      return (
        <div className="flex flex-col gap-2">
          {value.map((item, i) => (
            <div key={i} className="rounded border p-2">
              {typeof item === 'object' && item !== null
                ? Object.entries(item).map(([key, val]) => (
                    <div key={key} className="flex items-start gap-2">
                      <span className="font-medium">{key}:</span>
                      <span>{String(val)}</span>
                    </div>
                  ))
                : String(item)}
            </div>
          ))}
        </div>
      )
    }

    // For objects or other types, display as JSON string
    return <div className="whitespace-pre-wrap text-xs">{JSON.stringify(value, null, 2)}</div>
  }

  return columnFields.map((field) => {
    const { colStart, colSpan } = field.fieldConfig.layout.columnLayout ?? {}
    const gridColumn = colStart && colSpan ? `${colStart} / span ${colSpan}` : undefined

    return (
      <div key={field.propertyId} style={{ gridColumn }} className="flex flex-col gap-y-3">
        <div className="border-b pb-1 font-semibold italic text-muted-foreground">{field.title}</div>
        <div className="bg-offwhite overflow-hidden rounded-lg p-5 shadow">{renderFieldValue(field, content)}</div>
      </div>
    )
  })
}
